// The founder chart (FOUNDER-OS phase 1, design §1.2 and §2.4): KPIs › Chart in a portfolio
// company. The founder seat at the top, the portfolio's own seats under it (chief of staff, EA,
// shared services), and each company's CEO as a "board seat" card under the seat its link hangs
// from (`linked[].portfolio_seat_id`) — a linked reference to the company's own head seat, never
// a second seat. Drawn with the shared `OrgTree` (nested ul/li; the connectors are its CSS). The
// lens tabs read the card's projections: Health (band, score, trend), Money (budget vs actual),
// Headcount (people, seats, open seats, DEs) and Priorities (total, off, at risk); a kind the
// company withholds says so. Hover actions on a card (also on focus and touch): "Show <company>'s
// chart" expands that company's own chart below the card, read through its own `orgChart.get`
// (the viewer's own standing there: none reads "No access in <company>"), and, for the owner,
// "Move under…" re-parents the card in a dialog that keeps the viewer on the chart.
import { ApiError } from "@companyos/sdk";
import {
  AvatarTile,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  LensTabs,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  OrgTree,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, CornerDownRightIcon, NetworkIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { LinkedSeat, MeResponse, PortfolioChart, PortfolioSeat } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { HealthDot, SwitcherTile } from "../../shell/company-switcher";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { formatDate, formatMoney, formatScore } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { useSession } from "../use-feature";
import { RelationBadge, TrendArrow, useAgencyNames, Withheld } from "./portfolio-parts";
import { portfolioChartQuery, useMoveLinkedSeat } from "./portfolio-queries";

export const FOUNDER_LENSES = ["health", "money", "headcount", "priorities"] as const;
export type FounderLens = (typeof FOUNDER_LENSES)[number];
export const isFounderLens = (v: unknown): v is FounderLens =>
  typeof v === "string" && (FOUNDER_LENSES as readonly string[]).includes(v);

export const FOUNDER_LENS_LABELS: Record<FounderLens, string> = {
  health: "Health",
  money: "Money",
  headcount: "Headcount",
  priorities: "Priorities",
};

/** The projection kind each lens reads (a withheld kind reads "Withheld by <company>"). */
const LENS_KIND: Record<FounderLens, string> = {
  health: "kpi",
  money: "finance",
  headcount: "headcount",
  priorities: "goals",
};

/** The caller's account grant is `portfolio_viewer` (kpi, finance and goals only). */
const viewerLevel = (me: MeResponse) => me.portfolio?.level === "portfolio_viewer";

/** Visible on hover, keyboard focus and touch (never hidden from a phone). */
const HOVER_ACTION =
  "opacity-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100";

export function FounderChart({
  api,
  company,
  lens = "health",
  onLens,
}: {
  api: Api;
  company: CompanyAccess;
  lens?: FounderLens;
  onLens: (lens: FounderLens) => void;
}) {
  const chart = useQuery(portfolioChartQuery(api, company.id));
  const [moving, setMoving] = useState<LinkedSeat | null>(null);
  const data = chart.data;
  return (
    <div className="flex min-h-full min-w-0 flex-col" data-slot="founder-chart">
      <LocationHeader
        icon={NetworkIcon}
        crumbs={[
          {
            label: "KPIs",
            render: <Link to="/c/$slug/kpis" params={{ slug: company.slug }} search={keepFocus} />,
          },
        ]}
        title="Chart"
        {...(data ? { count: data.linked.length } : {})}
      />
      <div
        data-slot="chart-lens-bar"
        data-lens={lens}
        className="flex min-w-0 flex-col gap-0.5 border-b border-subtle bg-card px-6 pt-1 pb-1 max-md:px-4"
      >
        <LensTabs
          aria-label="Lens"
          value={lens}
          onValueChange={(v) => onLens(v as FounderLens)}
          tabs={FOUNDER_LENSES.map((l) => ({ value: l, label: FOUNDER_LENS_LABELS[l] }))}
        />
      </div>
      <div className="min-w-0 flex-1 overflow-auto">
        {chart.isPending ? (
          <div className="px-6 py-6">
            <Loading label="Loading the founder chart" rows={3} />
          </div>
        ) : chart.isError ? (
          <div className="px-6 py-6">
            <LoadError what="The founder chart" error={chart.error} />
          </div>
        ) : data ? (
          <>
            <OrgTree view="tree" aria-label="Founder chart" data-slot="founder-tree">
              <FounderTree api={api} company={company} data={data} lens={lens} onMove={setMoving} />
            </OrgTree>
            {data.linked.length === 0 ? (
              <p className="px-6 pb-6 text-center text-sm text-muted-foreground">
                No company is linked yet: add one from Focus.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
      {moving && data ? (
        <MoveDialog
          api={api}
          co={company.id}
          data={data}
          linked={moving}
          onClose={() => setMoving(null)}
        />
      ) : null}
    </div>
  );
}

/** The portfolio's seats as a tree, each with the linked cards that hang under it. */
function FounderTree({
  api,
  company,
  data,
  lens,
  onMove,
}: {
  api: Api;
  company: CompanyAccess;
  data: PortfolioChart;
  lens: FounderLens;
  onMove: (l: LinkedSeat) => void;
}) {
  const all = [...(data.founder_seat ? [data.founder_seat] : []), ...data.seats];
  const ids = new Set(all.map((s) => s.seat_id));
  const roots = all.filter((s) => !s.supervisor_seat_id || !ids.has(s.supervisor_seat_id));
  // A card whose seat is gone hangs under the founder (the API's default parent).
  const top = data.founder_seat?.seat_id ?? roots[0]?.seat_id ?? null;
  const linkedUnder = (id: string) =>
    data.linked.filter(
      (l) => l.portfolio_seat_id === id || (id === top && !ids.has(l.portfolio_seat_id)),
    );
  const card = (l: LinkedSeat) => (
    <LinkedNode
      key={l.linked_seat_id}
      api={api}
      company={company}
      linked={l}
      lens={lens}
      canEdit={!!data.can_edit}
      onMove={() => onMove(l)}
    />
  );
  const seen = new Set<string>();
  const node = (s: PortfolioSeat): ReactNode => {
    if (seen.has(s.seat_id)) return null;
    seen.add(s.seat_id);
    const kids = all.filter((x) => x.supervisor_seat_id === s.seat_id && x.seat_id !== s.seat_id);
    const cards = linkedUnder(s.seat_id);
    return (
      <li key={s.seat_id}>
        <SeatCard seat={s} founder={s.seat_id === data.founder_seat?.seat_id} />
        {kids.length || cards.length ? (
          <ul>
            {kids.map(node)}
            {cards.map(card)}
          </ul>
        ) : null}
      </li>
    );
  };
  // No seat at all (a portfolio still being set up): the cards stand on their own.
  return <>{roots.length ? roots.map(node) : data.linked.map(card)}</>;
}

function SeatCard({ seat, founder = false }: { seat: PortfolioSeat; founder?: boolean }) {
  return (
    <article
      aria-label={`${seat.title}${seat.holder_name ? `: ${seat.holder_name}` : ", open"}`}
      data-slot="portfolio-seat"
      className={cn(
        "flex w-60 items-center gap-2.5 rounded-lg border bg-card px-3.5 py-3 text-left shadow-xs",
        founder ? "border-primary/40 ring-2 ring-primary-soft" : "border-border",
        !seat.holder_name && "border-dashed",
      )}
    >
      <AvatarTile name={seat.holder_name ?? seat.title} kind="person" size="md" />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-semibold text-foreground">{seat.title}</span>
        <span className="truncate text-xs text-muted-foreground">{seat.holder_name ?? "Open"}</span>
      </div>
    </article>
  );
}

/** A company's board-seat card and, when expanded, the company's own chart under it. */
function LinkedNode({
  api,
  company,
  linked,
  lens,
  canEdit,
  onMove,
}: {
  api: Api;
  company: CompanyAccess;
  linked: LinkedSeat;
  lens: FounderLens;
  canEdit: boolean;
  onMove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const subtreeId = `subtree-${linked.company_id}`;
  return (
    <li data-slot="linked-seat" data-company={linked.slug}>
      <LinkedCard company={company} linked={linked} lens={lens}>
        <Button
          variant="ghost"
          size="xs"
          className={open ? undefined : HOVER_ACTION}
          aria-expanded={open}
          aria-controls={open ? subtreeId : undefined}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ChevronDownIcon aria-hidden="true" /> : <ChevronRightIcon aria-hidden="true" />}
          {open ? `Hide ${linked.name}'s chart` : `Show ${linked.name}'s chart`}
        </Button>
        {canEdit ? (
          <Button variant="ghost" size="xs" className={HOVER_ACTION} onClick={onMove}>
            <CornerDownRightIcon aria-hidden="true" />
            Move under…
          </Button>
        ) : null}
      </LinkedCard>
      {open ? <CompanySubtree api={api} id={subtreeId} linked={linked} /> : null}
    </li>
  );
}

function LinkedCard({
  company,
  linked,
  lens,
  children,
}: {
  company: CompanyAccess;
  linked: LinkedSeat;
  lens: FounderLens;
  children: ReactNode;
}) {
  const agencies = useAgencyNames();
  const { me } = useSession();
  const head = linked.head_seat;
  const de = head?.holder_kind === "digital_employee";
  const you = !!head?.holder_name && head.holder_name === me.user.name;
  const holder = head?.holder_name ? workerName(head.holder_name) : null;
  // No head seat: the company withholds its headcount, the viewer's grant does not read it (a
  // portfolio viewer reads kpi, finance and goals only), or there is genuinely none yet.
  const headState = head
    ? "shown"
    : linked.withheld?.includes("headcount")
      ? "withheld"
      : viewerLevel(me)
        ? "viewer"
        : "none";
  return (
    <article
      aria-label={linked.name}
      data-slot="linked-card"
      data-lens={lens}
      className="group/card flex w-72 flex-col rounded-lg border border-border bg-card text-left shadow-xs"
    >
      <div className="flex items-start gap-2.5 px-3.5 pt-3">
        <SwitcherTile name={linked.name} portfolio={false} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Link
            to="/c/$slug/portfolio/companies/$companyId"
            params={{ slug: company.slug, companyId: linked.company_id }}
            className="truncate text-sm font-semibold text-foreground hover:underline"
          >
            {linked.name}
          </Link>
          <RelationBadge relation={linked.relation} agency={agencies.get(linked.company_id)} />
        </div>
      </div>
      <div
        data-slot="head-seat"
        data-vacant={head && !head.holder_name ? "true" : undefined}
        className={cn(
          "mx-3.5 mt-3 flex items-center gap-2.5 rounded-md bg-sunken px-2.5 py-2",
          head && !head.holder_name && "border border-dashed border-strong bg-transparent",
        )}
      >
        <AvatarTile name={holder ?? head?.title ?? "?"} kind={de ? "agent" : "person"} size="sm" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-xs font-semibold text-foreground" data-head={headState}>
            {head ? (
              head.title
            ) : headState === "withheld" ? (
              <Withheld name={linked.name} />
            ) : headState === "viewer" ? (
              "Not shared with you"
            ) : (
              "No head seat yet"
            )}
          </span>
          <span className="inline-flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
            {head && holder ? (
              <>
                <WorkerBadge kind={de ? "de" : "person"} size="sm" />
                <span className="truncate">{you ? `${holder} (you)` : holder}</span>
              </>
            ) : head ? (
              <span className="italic">
                Open{head.open_since ? ` since ${formatDate(head.open_since)}` : ""}
              </span>
            ) : (
              "—"
            )}
          </span>
          {de && head?.supervisor_title ? (
            <span className="truncate text-xs text-faint">
              supervised by {head.supervisor_title}
            </span>
          ) : null}
        </div>
      </div>
      <div className="px-3.5 pt-2 text-xs" data-slot="lens-value">
        <LensValue lens={lens} linked={linked} viewer={viewerLevel(me)} />
      </div>
      <div className="flex flex-wrap gap-1 px-2 pt-1 pb-2">{children}</div>
    </article>
  );
}

/** What the card shows under the chart's lens, from the company's projections. */
export function LensValue({
  lens,
  linked,
  viewer = false,
}: {
  lens: FounderLens;
  linked: LinkedSeat;
  /** A portfolio viewer's grant reads kpi, finance and goals only: no headcount. */
  viewer?: boolean;
}) {
  if (linked.withheld?.includes(LENS_KIND[lens] as never)) return <Withheld name={linked.name} />;
  if (viewer && lens === "headcount")
    return <span className="text-muted-foreground">Not shared with you</span>;
  const none = <span className="text-muted-foreground">No numbers yet</span>;
  switch (lens) {
    case "health":
      if (linked.kpi_score == null && linked.band === null) return none;
      return (
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          <HealthDot band={linked.band} />
          <span className="font-medium text-foreground">
            {formatScore(linked.kpi_score ?? null)}
          </span>
          <TrendArrow trend={linked.trend ?? null} />
        </span>
      );
    case "money": {
      const f = linked.finance;
      if (!f) return none;
      const money = (n: number) => formatMoney(n, f.currency, { whole: true });
      if (f.budget_minor === null)
        return (
          <span className="tabular-nums text-secondary">
            {money(f.actual_minor)} spent · no budget
          </span>
        );
      const over = f.actual_minor > f.budget_minor;
      return (
        <span className="tabular-nums" data-over={over || undefined}>
          <span className={cn("font-medium", over ? "text-danger-text" : "text-foreground")}>
            {money(f.actual_minor)}
          </span>
          <span className="text-muted-foreground"> of {money(f.budget_minor)}</span>
        </span>
      );
    }
    case "headcount": {
      const h = linked.headcount;
      const a = linked.agents;
      return (
        <span className="tabular-nums text-secondary">
          {h.people} {h.people === 1 ? "person" : "people"} · {h.seats} seat
          {h.seats === 1 ? "" : "s"}
          {h.open_seats ? <span className="text-warning-text"> · {h.open_seats} open</span> : null}
          {a ? ` · DEs ${a.live}/${a.des} live` : null}
        </span>
      );
    }
    case "priorities": {
      const p = linked.priorities;
      if (p.total === 0) return <span className="text-muted-foreground">No priorities yet</span>;
      return (
        <span className="tabular-nums text-secondary">
          {p.total} priorit{p.total === 1 ? "y" : "ies"}
          {p.off ? <span className="text-danger-text"> · {p.off} off</span> : null}
          {p.at_risk ? <span className="text-warning-text"> · {p.at_risk} at risk</span> : null}
        </span>
      );
    }
  }
}

type TreeSeat = {
  id: string;
  title: string;
  status: string;
  supervisor_seat_id: string | null;
  holders: { display_name: string; membership_kind: string }[];
};

/** The company's own chart under its card, through the viewer's own standing there. */
function CompanySubtree({ api, id, linked }: { api: Api; id: string; linked: LinkedSeat }) {
  const chart = useQuery({ ...orgChartQuery(api, linked.company_id), retry: false });
  const note = (children: ReactNode) => (
    <ul id={id} aria-label={`${linked.name}'s chart`}>
      <li>
        <p className="w-60 rounded-md border border-dashed border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          {children}
        </p>
      </li>
    </ul>
  );
  if (chart.isPending) return note(`Loading ${linked.name}'s chart…`);
  if (chart.isError) {
    const status = chart.error instanceof ApiError ? chart.error.status : 0;
    if (status === 403 || status === 404) return note(`No access in ${linked.name}`);
    return note(`${linked.name}'s chart could not be loaded.`);
  }
  const seats = (chart.data.seats as TreeSeat[]).filter(
    (s) => s.status !== "closed" && s.status !== "proposed",
  );
  if (seats.length === 0) return note(`${linked.name} has no seats on its chart yet.`);
  const ids = new Set(seats.map((s) => s.id));
  const roots = seats.filter((s) => !s.supervisor_seat_id || !ids.has(s.supervisor_seat_id));
  const seen = new Set<string>();
  const node = (s: TreeSeat): ReactNode => {
    if (seen.has(s.id)) return null;
    seen.add(s.id);
    const kids = seats.filter((x) => x.supervisor_seat_id === s.id);
    return (
      <li key={s.id}>
        <MiniSeat seat={s} />
        {kids.length ? <ul>{kids.map(node)}</ul> : null}
      </li>
    );
  };
  return (
    <ul id={id} aria-label={`${linked.name}'s chart`}>
      {roots.map(node)}
    </ul>
  );
}

function MiniSeat({ seat }: { seat: TreeSeat }) {
  const holder = seat.holders[0];
  const de = holder?.membership_kind === "digital_employee";
  return (
    <div
      data-slot="tree-seat"
      className={cn(
        "flex w-48 items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-left shadow-xs",
        holder ? "border-border" : "border-dashed border-strong",
      )}
    >
      <AvatarTile
        name={workerName(holder?.display_name ?? seat.title)}
        kind={de ? "agent" : "person"}
        size="xs"
      />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-xs font-medium text-foreground">{seat.title}</span>
        <span className="inline-flex min-w-0 items-center gap-1 text-2xs text-muted-foreground">
          {de ? <WorkerBadge kind="de" size="sm" /> : null}
          <span className="truncate">{holder ? workerName(holder.display_name) : "Open"}</span>
        </span>
      </div>
    </div>
  );
}

/** "Move under…": pick the portfolio seat the company's card hangs from. */
function MoveDialog({
  api,
  co,
  data,
  linked,
  onClose,
}: {
  api: Api;
  co: string;
  data: PortfolioChart;
  linked: LinkedSeat;
  onClose: () => void;
}) {
  const id = useId();
  const seats = [...(data.founder_seat ? [data.founder_seat] : []), ...data.seats];
  const [seat, setSeat] = useState(linked.portfolio_seat_id);
  const move = useMoveLinkedSeat(api, co);
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move {linked.name} under…</DialogTitle>
          <DialogDescription>
            The seat on your chart this company's CEO card reports to.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-seat`}>Seat</Label>
          <NativeSelect
            id={`${id}-seat`}
            value={seat}
            className="w-full"
            onChange={(e) => setSeat(e.target.value)}
          >
            {seats.map((s) => (
              <NativeSelectOption key={s.seat_id} value={s.seat_id}>
                {s.holder_name ? `${s.title} · ${s.holder_name}` : `${s.title} · Open`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        {move.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            Could not move the card. Try again.
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={move.isPending || seat === linked.portfolio_seat_id}
            onClick={() =>
              move.mutate(
                { id: linked.linked_seat_id, portfolio_seat_id: seat },
                { onSuccess: onClose },
              )
            }
          >
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A company's own org chart as a read-only nested list (its `orgChart.get`): the drill-down. */
export function CompanyOrgTree({
  api,
  companyId,
  name,
}: {
  api: Api;
  companyId: string;
  name: string;
}) {
  const chart = useQuery(orgChartQuery(api, companyId));
  if (chart.isPending) return <Loading label={`Loading ${name}'s chart`} rows={3} />;
  if (chart.isError) return <LoadError what={`${name}'s chart`} error={chart.error} />;
  const seats: TreeSeat[] = chart.data.seats;
  if (seats.length === 0)
    return <p className="text-sm text-muted-foreground">{name} has no seats on its chart yet.</p>;
  const ids = new Set(seats.map((s) => s.id));
  const roots = seats.filter((s) => !s.supervisor_seat_id || !ids.has(s.supervisor_seat_id));
  const children = (id: string) => seats.filter((s) => s.supervisor_seat_id === id);
  const render = (list: TreeSeat[], depth: number) => (
    <ul
      className={cn("flex flex-col gap-1", depth > 0 && "ml-3 border-l border-subtle pl-3")}
      aria-label={depth === 0 ? `${name}'s chart` : undefined}
    >
      {list.map((s) => {
        const holder = s.holders[0];
        return (
          <li key={s.id} data-slot="tree-seat">
            <div className="flex min-w-0 items-center gap-2 py-1">
              <AvatarTile
                name={workerName(holder?.display_name ?? s.title)}
                kind={holder?.membership_kind === "digital_employee" ? "agent" : "person"}
                size="xs"
              />
              <span className="truncate text-sm font-medium text-foreground">{s.title}</span>
              {holder?.membership_kind === "digital_employee" ? (
                <WorkerBadge kind="de" size="sm" />
              ) : null}
              <span className="truncate text-xs text-muted-foreground">
                {holder ? workerName(holder.display_name) : s.status === "open" ? "Open" : "—"}
              </span>
            </div>
            {children(s.id).length ? render(children(s.id), depth + 1) : null}
          </li>
        );
      })}
    </ul>
  );
  return render(roots, 0);
}
